import { axiosInstance } from "@/plugins/https";
import api from "@/_helpers/api";
import { defineStore } from "pinia";
import { SiemConnectorFormat } from "@/constants/connectors.ts";
import { ServiceStatus } from "@/constants/cloud-apps.ts";
import { SubscriptionModule } from "@/constants/workplaces.ts";
import { SiemErrorType, SyslogFacility } from "@/constants/syslogs.ts";
import { type SiemConnectorBaseData } from "@/_store/connectors/siem.module.ts";
import { denormalizeSyslog, normalizeSyslogs } from "@/_store/connectors/adapters.ts";
import { StoreModule } from "@/constants/store.ts";
import { useSnackbarStore } from "@/_store/snackbar.module.ts";
import { i18n } from "@/plugins/i18n.ts";

export interface SyslogModule {
  module: SubscriptionModule;
  facility: SyslogFacility;
}

export interface SyslogRaw extends SiemConnectorBaseData {
  syslog: {
    url: string; // domain:port
    certificate?: string;
    modules: SyslogModule[];
    tenantLabel: string | null;
    lastUsed: string;
  };
}

export interface Syslog {
  id: string;
  name: string;
  destination: string;
  port: string;
  workspaceId: string;
  errorType: SiemErrorType | null;
  status: ServiceStatus;
  certificate: File | string;
  modules: (SyslogModule & { enabled?: boolean })[];
  isMultiTenant: boolean;
  tenantLabel: string;
  lastUsed: string;
}

interface SyslogsState {
  syslogs: Syslog[];
  loading: boolean;
  showSkeletonLoader: boolean;
}

const defaultSyslogsState = {
  syslogs: [],
  loading: false,
  showSkeletonLoader: false,
};

export const useSyslogsStore = defineStore(StoreModule.SYSLOGS, {
  state: (): SyslogsState => ({ ...defaultSyslogsState }),
  actions: {
    resetState() {
      this.$reset();
    },
    async getSyslogs(showSkeletonLoader: boolean = true): Promise<void> {
      if (showSkeletonLoader) {
        this.showSkeletonLoader = true;
      } else {
        this.loading = true;
      }

      try {
        const response = await axiosInstance.request(api.siemConnectors());
        const syslogs = response.data.filter(
          (item: SyslogRaw) => item.format === SiemConnectorFormat.SYSLOG
        );

        this.syslogs = normalizeSyslogs(syslogs);
      } catch (err) {
        console.error(err);
      }

      this.showSkeletonLoader = false;
      this.loading = false;
    },
    async addSyslog(data: Syslog): Promise<void> {
      try {
        this.loading = true;

        const { id, ...dataForBE } = await denormalizeSyslog(data);

        const request = {
          ...api.siemConnectors(),
          data: dataForBE,
          method: "post",
        };

        await axiosInstance.request(request);
      } catch (err) {
        console.error(err);
        throw err;
      } finally {
        this.loading = false;
        await this.getSyslogs(false);
      }
    },
    async updateSyslog(data: Syslog): Promise<void> {
      try {
        this.loading = true;

        const dataForBE = await denormalizeSyslog(data);

        const request = {
          ...api.siemConnector(data.id),
          data: dataForBE,
          method: "put",
        };

        await axiosInstance.request(request);
      } catch (err) {
        console.error(err);
        throw err;
      } finally {
        await this.getSyslogs(false);
        this.loading = false;
      }
    },
    async deleteSyslog(data: Syslog): Promise<void> {
      this.loading = true;

      try {
        const request = {
          ...api.siemConnector(data.id),
          method: "delete",
        };

        await axiosInstance.request(request);
      } catch (error) {
        console.error(error);
      } finally {
        await this.getSyslogs(false);
        this.loading = false;
      }
    },
    async syncSyslog(data: Syslog): Promise<void> {
      try {
        this.loading = true;

        const dataForBE = await denormalizeSyslog(data);

        const request = {
          ...api.syncSiemConnector(),
          data: dataForBE,
          method: "post",
        };

        await axiosInstance.request(request);

        const snackbarModule = useSnackbarStore();

        snackbarModule.addGenericSuccess(
          i18n.global.t("snackbar.messages.connectors.syncCompleted")
        );
      } catch (err) {
        console.error(err);
      } finally {
        await this.getSyslogs(false);
        this.loading = false;
      }
    },
  },
});
